<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Transactions"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasTransactionsData"
            :has-errors="hasSectionErrors"
          />
          Transactions
        </span>
      </template>
      <validation-observer>
        <b-row v-if="parcel.transaction_data">
          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Total amount invoiced"
            >
              <b-form-group>
                <label>
                  Total amount invoiced
                  <small class="text-muted">1406000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Total amount invoiced for the shipment'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.transaction_data.invoice_amount"
                  v-b-tooltip.hover.top="getIssueMessage('transaction_data.invoice_amount')"
                  :class="getIssueClass('transaction_data.invoice_amount')"
                  type="number"
                  step="0.01"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Invoice currency"
              rules=""
            >
              <b-form-group>
                <label>
                  Invoice currency <small class="text-muted">1405000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Currency used for the invoice'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="parcel.transaction_data.invoice_currency"
                  :classifier-key="classifierKeys.currencies"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Gross mass"
            >
              <b-form-group>
                <label>
                  Gross mass
                  <small class="text-muted">1804000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Total gross mass of goods in kilograms'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-input-group append="kg">
                  <b-form-input
                    v-model="parcel.goods_shipment_gross_weight"
                    v-b-tooltip.hover.top="getIssueMessage('goods_shipment_gross_weight')"
                    :class="getIssueClass('goods_shipment_gross_weight')"
                    type="number"
                    step="0.001"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Total net mass"
            >
              <b-form-group>
                <label>
                  Total net mass
                  <small class="text-muted">180300000B</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Total net mass of goods in kilograms'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-input-group append="kg">
                  <b-form-input
                    v-model="parcel.transaction_data.total_net_weight"
                    v-b-tooltip.hover.top="getIssueMessage('transaction_data.total_net_weight')"
                    :class="getIssueClass('transaction_data.total_net_weight')"
                    type="number"
                    step="0.001"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Nature of transaction"
            >
              <b-form-group>
                <label>
                  Nature of transaction
                  <small class="text-muted">9905000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Nature of the transaction'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="parcel.transaction_data.nature"
                  :classifier-key="classifierKeys.transactionNatures"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BInputGroup,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import { CUSTOMS_CLASSIFIER_KEYS } from '@/libs/customsClassifiers'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    ValidationObserver,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
    ClassifierSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      classifierKeys: CUSTOMS_CLASSIFIER_KEYS,
    }
  },
  computed: {
    hasTransactionsData() {
      if (!this.parcel) {
        return false
      }
      if (this.parcel.goods_shipment_gross_weight) {
        return true
      }
      if (!this.parcel.transaction_data) {
        return false
      }
      const {
        invoice_amount: invoiceAmount,
        invoice_currency: invoiceCurrency,
        total_net_weight: totalNetWeight,
        nature: transactionNature,
      } = this.parcel.transaction_data
      return !!(
        invoiceAmount
        || invoiceCurrency
        || totalNetWeight
        || transactionNature
      )
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('transaction_data.') || key === 'goods_shipment_gross_weight')
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
